import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Search, Sparkles, Tag, Trash2, X, Save, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  listPersonalNotes, listPersonalNoteTags, createPersonalNote, updatePersonalNote, deletePersonalNote,
  getPersonalNoteThink, sendPersonalNoteThink, clearPersonalNoteThink, formatApiError,
} from "@/lib/inmindApi";
import ThinkPanel from "@/components/ThinkPanel";

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);   // { id?, title, content, tags[] }
  const [thinkFor, setThinkFor] = useState(null); // a note

  const load = async () => {
    setLoading(true);
    try {
      const [n, t] = await Promise.all([
        listPersonalNotes({ q: q || undefined, tag: activeTag || undefined }),
        listPersonalNoteTags(),
      ]);
      setNotes(n); setTags(t);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load notes");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, activeTag]);

  const startNew = () => setEditor({ title: "", content: "", tags: [] });
  const openEdit = (n) => setEditor({ id: n.id, title: n.title, content: n.content, tags: n.tags || [] });

  const save = async () => {
    if (!editor) return;
    const payload = {
      title: editor.title.trim(), content: editor.content.trim(), tags: editor.tags,
    };
    if (!payload.title || !payload.content) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      if (editor.id) {
        await updatePersonalNote(editor.id, payload);
        toast.success("Note saved");
      } else {
        await createPersonalNote(payload);
        toast.success("Note created");
      }
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note? Its Think chat is cleared too.")) return;
    try {
      await deletePersonalNote(id);
      toast.success("Deleted");
      if (thinkFor?.id === id) setThinkFor(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="notes-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
            Personal notes
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl mt-1 text-[var(--inmind-text)] tracking-tight">
            Reflect, think, learn
          </h2>
        </div>
        <Button
          onClick={startNew}
          data-testid="new-note-btn"
          className="rounded-full h-11 px-4 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
        >
          <Plus className="w-4 h-4" strokeWidth={1.9} /> <span className="hidden sm:inline">New note</span>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--inmind-muted)]" strokeWidth={1.7} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your notes…" data-testid="notes-search" className="pl-9 h-11 rounded-xl" />
        </div>
        {tags.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="notes-tags">
            <button
              onClick={() => setActiveTag("")}
              data-testid="tag-all"
              className={`text-xs uppercase tracking-wide font-semibold rounded-full px-3 py-1.5 shrink-0 transition-colors ${
                !activeTag ? "bg-[var(--inmind-primary)] text-white" : "bg-white text-[var(--inmind-text-secondary)] border border-black/10"
              }`}
            >
              All
            </button>
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(activeTag === t ? "" : t)}
                data-testid={`tag-${t}`}
                className={`inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 shrink-0 transition-colors ${
                  activeTag === t
                    ? "bg-[var(--inmind-primary)] text-white"
                    : "bg-white text-[var(--inmind-text-secondary)] border border-black/10 hover:bg-black/5"
                }`}
              >
                <Tag className="w-3 h-3" strokeWidth={1.7} /> {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[var(--inmind-muted)]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : notes.length === 0 ? (
        <EmptyState onCreate={startNew} hasFilter={!!q || !!activeTag} />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="notes-list">
          {notes.map((n) => (
            <NoteCard key={n.id} note={n} onOpen={openEdit} onAsk={setThinkFor} onDelete={remove} />
          ))}
        </ul>
      )}

      <Sheet open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="note-editor">
          <SheetHeader className="sr-only">
            <SheetTitle>{editor?.id ? "Edit note" : "New note"}</SheetTitle>
            <SheetDescription>Write or update a personal reflection.</SheetDescription>
          </SheetHeader>
          {editor && <Editor value={editor} onChange={setEditor} onSave={save} onClose={() => setEditor(null)} saving={saving} />}
        </SheetContent>
      </Sheet>

      <Sheet open={!!thinkFor} onOpenChange={(o) => !o && setThinkFor(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col bg-transparent border-none shadow-none" data-testid="note-think-sheet">
          <SheetHeader className="sr-only">
            <SheetTitle>Ask Think about {thinkFor?.title || "this note"}</SheetTitle>
            <SheetDescription>An AI thinking partner anchored to this note.</SheetDescription>
          </SheetHeader>
          {thinkFor && (
            <div className="h-full p-3 sm:p-4">
              <ThinkPanel
                key={thinkFor.id}
                subtitle={thinkFor.title}
                title="Think · about this note"
                emptyHint="Want to sharpen this thought? Share what you mean and I'll help you go deeper."
                placeholder="Ask Think about this note…"
                prompts={[
                  "Help me sharpen this theory",
                  "Give me a real-life example",
                  "What might I be missing?",
                  "Polish this note for clarity",
                ]}
                api={{
                  get: () => getPersonalNoteThink(thinkFor.id),
                  send: (content) => sendPersonalNoteThink(thinkFor.id, content),
                  clear: () => clearPersonalNoteThink(thinkFor.id),
                }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function EmptyState({ onCreate, hasFilter }) {
  return (
    <div className="text-center py-16 bg-white rounded-2xl border border-black/5 shadow-[0_4px_18px_rgba(0,0,0,0.03)]" data-testid="notes-empty">
      <Sparkles className="w-8 h-8 text-[var(--inmind-primary)] mx-auto mb-3" strokeWidth={1.6} />
      <p className="font-serif text-xl text-[var(--inmind-text)]">
        {hasFilter ? "Nothing matches" : "Start with one small thought"}
      </p>
      <p className="text-sm text-[var(--inmind-text-secondary)] mt-1 max-w-xs mx-auto">
        {hasFilter
          ? "Try clearing your search or tag filter."
          : "Capture a reflection, theory or question. Think — your AI partner — will help you sharpen it."}
      </p>
      <Button
        onClick={onCreate}
        data-testid="empty-create-btn"
        className="mt-5 rounded-full h-10 px-5 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
      >
        <Plus className="w-4 h-4" strokeWidth={1.9} /> New note
      </Button>
    </div>
  );
}

function NoteCard({ note, onOpen, onAsk, onDelete }) {
  const preview = useMemo(() => (note.content || "").slice(0, 220), [note.content]);
  return (
    <li
      onClick={() => onOpen(note)}
      data-testid={`note-${note.id}`}
      className="bg-white rounded-2xl border border-black/5 p-4 shadow-[0_4px_18px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-shadow cursor-pointer flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-serif text-lg text-[var(--inmind-text)] leading-snug truncate flex-1">{note.title}</h3>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
          data-testid={`delete-${note.id}`}
          aria-label="Delete"
          className="text-[var(--inmind-muted)] hover:text-red-500 transition-colors shrink-0"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />
        </button>
      </div>
      <p className="text-sm text-[var(--inmind-text-secondary)] line-clamp-3 leading-relaxed">{preview}</p>
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="flex flex-wrap gap-1.5 min-w-0">
          {(note.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider font-semibold text-[var(--inmind-muted)] bg-[var(--inmind-card-subtle)] rounded-full px-2 py-0.5 truncate">
              {t}
            </span>
          ))}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onAsk(note); }}
          data-testid={`ask-${note.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 bg-[var(--inmind-primary)]/10 text-[var(--inmind-primary)] hover:bg-[var(--inmind-primary)]/20 transition-colors shrink-0"
        >
          <Sparkles className="w-3 h-3" strokeWidth={1.9} /> Ask Think
        </button>
      </div>
    </li>
  );
}

function Editor({ value, onChange, onSave, onClose, saving }) {
  const [tagInput, setTagInput] = useState("");
  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (!t) return;
    if ((value.tags || []).includes(t)) { setTagInput(""); return; }
    onChange({ ...value, tags: [...(value.tags || []), t] });
    setTagInput("");
  };
  const removeTag = (t) => onChange({ ...value, tags: (value.tags || []).filter((x) => x !== t) });

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center justify-between px-5 py-4 border-b border-black/5">
        <div className="flex items-center gap-2">
          <Pencil className="w-4 h-4 text-[var(--inmind-muted)]" strokeWidth={1.7} />
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
            {value.id ? "Edit note" : "New note"}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="editor-close"
          className="text-[var(--inmind-muted)] hover:text-[var(--inmind-text)] transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <Input
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          placeholder="Title"
          data-testid="editor-title"
          className="text-lg font-serif h-12 rounded-xl"
        />
        <Textarea
          value={value.content}
          onChange={(e) => onChange({ ...value, content: e.target.value })}
          placeholder="Pour your thoughts here — reflections, theories, learnings, questions…"
          data-testid="editor-content"
          className="min-h-[260px] rounded-xl text-sm leading-relaxed"
        />
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-2">Tags</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(value.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 bg-[var(--inmind-card-subtle)] text-[var(--inmind-text-secondary)]" data-testid={`tag-chip-${t}`}>
                {t}
                <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`} className="hover:text-red-500">
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
            placeholder="Add a tag and hit Enter…"
            data-testid="editor-tag-input"
            className="h-10 rounded-xl"
          />
        </div>
      </div>
      <footer className="px-5 py-4 border-t border-black/5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose} data-testid="editor-cancel" className="rounded-xl text-[var(--inmind-text-secondary)]">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={saving}
          data-testid="editor-save"
          className="rounded-xl bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.8} />}
          Save
        </Button>
      </footer>
    </div>
  );
}
